// services/letterheadService.js
//
// Cliente do endpoint /api/letterhead.

import http from "@/services/http";
import { getMyClinics } from "@/services/clinicService";

/**
 * Retorna o timbre do dono especificado (ou do usuário autenticado se nada vier).
 * @param {Object} params  { owner_type?: 'user'|'clinic', owner_id?: number }
 * @returns {Promise<Object|null>}  payload do timbre ou null
 */
export async function getLetterhead(params = {}) {
  const { data } = await http.get("/api/letterhead", { params });
  return data?.letterhead || null;
}

/**
 * Faz upload (cria ou substitui) o timbre.
 * @param {Object} args
 * @param {Blob} args.imageBlob       PNG/JPG (obrigatório)
 * @param {Blob} [args.pdfBlob]       PDF original (opcional)
 * @param {string} [args.fileName]    Nome de arquivo p/ exibição
 * @param {string} [args.ownerType]   'user' | 'clinic'
 * @param {number} [args.ownerId]
 * @returns {Promise<Object>} timbre serializado
 */
export async function uploadLetterhead({
  imageBlob,
  pdfBlob,
  fileName,
  ownerType,
  ownerId,
}) {
  const form = new FormData();
  form.append("image", imageBlob, fileName || "letterhead.png");
  if (pdfBlob) {
    form.append("pdf", pdfBlob, "letterhead.pdf");
  }
  if (ownerType) form.append("owner_type", ownerType);
  if (ownerId != null) form.append("owner_id", String(ownerId));

  const { data } = await http.post("/api/letterhead", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data?.letterhead;
}

export async function deleteLetterhead(id) {
  await http.delete(`/api/letterhead/${id}`);
}

// ────────────────────────────────────────────────────────────────────────────
// Timbre escolhido para as prescrições
//
// O usuário pode ter um timbre pessoal e um por clínica. Qual deles vale na
// hora de imprimir/baixar é uma ESCOLHA dele, então fica guardada como
// preferência do usuário — sem isso a escolha feita no gerenciador de timbre
// se perdia ao fechar o diálogo e tudo caía no timbre pessoal.
// ────────────────────────────────────────────────────────────────────────────

const LETTERHEAD_PREF_KEY = "prescription_letterhead";

/** @returns {Promise<{owner_type: string, owner_id: number|null}|null>} */
export async function getLetterheadPreference() {
  try {
    const { data } = await http.get(
      `/api/user-preferences/${LETTERHEAD_PREF_KEY}`,
    );
    const value = data?.value;
    return value?.owner_type ? value : null;
  } catch {
    // Preferência é conveniência: se falhar, a resolução cai no fallback.
    return null;
  }
}

export async function saveLetterheadPreference({ ownerType, ownerId }) {
  await http.put(`/api/user-preferences/${LETTERHEAD_PREF_KEY}`, {
    value: { owner_type: ownerType, owner_id: ownerId ?? null },
  });
}

/**
 * Resolve o timbre que deve ser aplicado nas prescrições, nesta ordem:
 *   1. o que o usuário escolheu no gerenciador de timbre;
 *   2. o timbre do próprio usuário;
 *   3. o de alguma clínica da qual ele participa.
 *
 * @returns {Promise<Object|null>} timbre serializado (com content_area) ou null
 */
export async function resolvePreferredLetterhead() {
  const preference = await getLetterheadPreference();

  if (preference?.owner_type === "clinic" && preference.owner_id) {
    const fromClinic = await getLetterhead({
      owner_type: "clinic",
      owner_id: preference.owner_id,
    }).catch(() => null);
    if (fromClinic) return fromClinic;
    // Escolha aponta pra uma clínica sem timbre (ou da qual o usuário saiu):
    // não trava a impressão, segue para os fallbacks.
  }

  // Escolha explícita pelo perfil dispensa procurar timbre de clínica.
  const own = await getLetterhead().catch(() => null);
  if (own) return own;
  if (preference?.owner_type === "user") return null;

  // Sem preferência e sem timbre pessoal: usa o da clínica, se houver algum.
  const clinics = await getMyClinics().catch(() => null);
  for (const clinic of clinics || []) {
    const fromClinic = await getLetterhead({
      owner_type: "clinic",
      owner_id: clinic.id,
    }).catch(() => null);
    if (fromClinic) return fromClinic;
  }

  return null;
}

/**
 * Atualiza só a área de conteúdo (onde o texto da prescrição é escrito sobre o
 * timbre), sem reenviar a imagem.
 * @param {number} id
 * @param {Object|null} contentArea  { top, left, width, height } em % (0-100), ou null pra padrão
 * @returns {Promise<Object>} timbre serializado
 */
export async function updateLetterheadArea(id, contentArea) {
  const { data } = await http.put(`/api/letterhead/${id}/area`, {
    content_area: contentArea,
  });
  return data?.letterhead;
}

/**
 * Fetcha a imagem em blob (autenticada) e devolve um Object URL pronto pra
 * usar em <img :src=""> ou como background CSS.
 *
 * Caller fica responsável por revogar via URL.revokeObjectURL.
 */
export async function fetchLetterheadImageUrl(id) {
  const res = await http.get(`/api/letterhead/image/${id}`, {
    responseType: "blob",
  });
  return URL.createObjectURL(res.data);
}
